import { useEffect } from 'react';
import {
  gsap,
  setupGsap,
  prefersReducedMotion,
} from '../motion/gsapSetup';

/**
 * Pin a section and scrub through step panels with synchronized progress navigation.
 * Expects `.pin-story-step` children inside the pin container.
 */
const usePinnedStory = (sectionRef, pinRef, options = {}) => {
  const { endMultiplier = 1.3 } = options;

  useEffect(() => {
    setupGsap();
    const section = sectionRef?.current;
    const pin = pinRef?.current;
    if (!section || !pin) return undefined;

    const steps = pin.querySelectorAll('.pin-story-step');
    const navItems = pin.querySelectorAll('.pin-step-nav-btn');
    const progressFill = pin.querySelector('.pin-step-progress-fill');
    if (!steps.length) return undefined;

    const totalSteps = steps.length;
    const mm = gsap.matchMedia();

    mm.add('(min-width: 768px)', () => {
      if (prefersReducedMotion()) return;

      // Set initial state for all step cards
      gsap.set(steps, { opacity: 0, y: 36, scale: 0.98, pointerEvents: 'none' });
      gsap.set(steps[0], { opacity: 1, y: 0, scale: 1, pointerEvents: 'auto' });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: () => `+=${window.innerHeight * totalSteps * 0.85 * endMultiplier}`,
          pin: true,
          scrub: 0.8,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            if (progressFill) {
              progressFill.style.width = `${Math.min(100, Math.max(0, self.progress * 100))}%`;
            }
            if (navItems.length) {
              const activeIndex = Math.min(
                totalSteps - 1,
                Math.floor(self.progress * (totalSteps - 0.05))
              );
              navItems.forEach((btn, idx) => {
                if (idx === activeIndex) {
                  btn.classList.add('is-active');
                } else {
                  btn.classList.remove('is-active');
                }
              });
            }
          },
        },
      });

      steps.forEach((step, index) => {
        if (index === 0) return;
        const prev = steps[index - 1];
        const timePos = index - 0.4;

        tl.to(
          prev,
          {
            opacity: 0,
            y: -28,
            scale: 0.96,
            duration: 0.45,
            pointerEvents: 'none',
            ease: 'power2.inOut',
          },
          timePos
        );

        tl.to(
          step,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.45,
            pointerEvents: 'auto',
            ease: 'power2.out',
          },
          timePos + 0.15
        );
      });
    });

    mm.add('(max-width: 767px)', () => {
      gsap.set(steps, { clearProps: 'all', opacity: 1, y: 0, scale: 1, pointerEvents: 'auto' });
    });

    return () => mm.revert();
  }, [sectionRef, pinRef, endMultiplier]);
};

export default usePinnedStory;

